import { useParams } from 'react-router-dom';
import { useMedia } from '../hooks/useMedia';
import { useToast } from '../hooks/useToast';
import '../styles/editor.css';

export default function MediaLibrary() {
  const { siteId } = useParams<{ siteId: string }>();
  const { media, loading, error, uploading, uploadProgress, upload, remove } =
    useMedia(siteId);
  const { addToast } = useToast();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      try {
        await upload(file);
        addToast('success', `Uploaded ${file.name}`);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Upload failed');
      }
    }
    // Clear the input
    e.target.value = '';
  };

  const handleDelete = async (mediaId: string, filename: string) => {
    if (!confirm(`Delete "${filename}"?`)) return;
    try {
      await remove(mediaId);
      addToast('success', 'Media deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Media Library</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Media Library</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Media Library</h1>
        <label className="btn btn-primary">
          {uploading ? `Uploading ${uploadProgress}%` : '+ Upload'}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </label>
      </div>

      {media.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg></div>
          <h2>No media yet</h2>
          <p>Upload images to use in your site content.</p>
          <label className="btn btn-primary">
            Upload your first image
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      ) : (
        <div className="media-grid">
          {media.map((item) => (
            <div key={item.mediaId} className="media-item">
              <div className="media-item-image">
                <img
                  src={item.url}
                  alt={item.filename}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML =
                      '<div class="media-placeholder"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>';
                  }}
                />
              </div>
              <div className="media-item-info">
                <span className="media-item-name" title={item.filename}>
                  {item.filename}
                </span>
                <span className="media-item-size">{formatSize(item.size)}</span>
              </div>
              <div className="media-item-actions">
                <button
                  className="btn-icon"
                  onClick={() => {
                    navigator.clipboard.writeText(item.key);
                    addToast('info', 'Copied media key to clipboard');
                  }}
                  title="Copy key"
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => handleDelete(item.mediaId, item.filename)}
                  title="Delete"
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
